import { useMemo } from 'react';

import { useWallParticipantsStore, type WallBoard, type WallShape } from '@walls/store';

export function useShapePermissions(board: WallBoard, shape: WallShape) {
  const currentParticipantId = useWallParticipantsStore((state) => state.currentParticipantId);

  return useMemo(
    () => getShapePermissions(board, shape, currentParticipantId),
    [board, currentParticipantId, shape]
  );
}

export function getShapePermissions(
  board: WallBoard,
  shape: WallShape,
  currentParticipantId: string | null
) {
  const hasParticipant = Boolean(currentParticipantId);
  const hasWorkspaceAccess = Boolean(
    board.isOwner || (hasParticipant && (board.isMember || board.canCollaborate))
  );
  const canSelectBoardAccess = Boolean(board.isOwner || board.isMember || board.canCollaborate);
  const ownsShape = Boolean(currentParticipantId && shape.ownerId === currentParticipantId);
  const canEdit = Boolean(board.isOwner || ownsShape);
  const canMove =
    board.allowDrawingTools && hasWorkspaceAccess && canSelectBoardAccess && !shape.isLocked;
  const canModify =
    board.allowDrawingTools && canEdit && hasWorkspaceAccess && canSelectBoardAccess;
  const canSelect =
    board.allowDrawingTools &&
    canSelectBoardAccess &&
    hasWorkspaceAccess &&
    (!shape.isLocked || (board.allowCardUnlocking && canEdit));

  return {
    canDelete: canModify,
    canEdit,
    canInteract: hasWorkspaceAccess && canSelectBoardAccess,
    canLock: board.allowCardLocking && canModify && !shape.isLocked,
    canMove,
    canResize: canModify && !shape.isLocked,
    canSelect,
    canStyle: canModify && !shape.isLocked,
    canUnlock: board.allowCardUnlocking && canModify && shape.isLocked,
  };
}
